import type { RouteLocationRaw } from 'vue-router';

import i18n from '@/i18n';

import type {
	FollowingBaseInput,
	FollowingLineGeneratorResult,
	FollowingRightAction,
	FollowingSegment,
} from '@/components/following/types';

import {
	createActionSegment,
	createLinkSegment,
	createReadonlyLabelSegment,
	createTextSegment,
} from './segmentFactory';

export enum ProfilFollowingType {
	CVTHEQUE_GROUP_ADDED = 'cvthequeGroupAdded',
	NON_DELEGABLE = 'nonDelegable',
	DELEGABLE = 'delegable',
	CV_UPDATED = 'cvUpdated',
	CV_DELETED = 'cvDeleted',
	CV_ANONYMOUS_UPDATED = 'cvAnonymousUpdated',
	CV_ANONYMOUS_DELETED = 'cvAnonymousDeleted',
	DATA_UPDATED = 'dataUpdated',
}

interface TypeConfig {
	icon: string;
	segmentIcon?: string;
	segmentIconColor?: string;
}

const TYPE_CONFIGS: Record<ProfilFollowingType, TypeConfig> = {
	[ProfilFollowingType.CVTHEQUE_GROUP_ADDED]: {
		icon: 'user-circle-fill',
		segmentIconColor: 'var(--neutral-700, #323848)',
	},
	[ProfilFollowingType.NON_DELEGABLE]: {
		icon: 'user-circle-fill',
		segmentIcon: 'wrong-way',
		segmentIconColor: 'var(--red-base, #E51940)',
	},
	[ProfilFollowingType.DELEGABLE]: {
		icon: 'user-circle-fill',
		segmentIcon: 'check',
		segmentIconColor: 'var(--neutral-700, #323848)',
	},
	[ProfilFollowingType.CV_UPDATED]: {
		icon: 'user-circle-fill',
		segmentIcon: 'doc-cv',
		segmentIconColor: 'var(--neutral-700, #323848)',
	},
	[ProfilFollowingType.CV_DELETED]: {
		icon: 'user-circle-fill',
		segmentIcon: 'doc-cv',
		segmentIconColor: 'var(--neutral-700, #323848)',
	},
	[ProfilFollowingType.CV_ANONYMOUS_UPDATED]: {
		icon: 'user-circle-fill',
		segmentIcon: 'doc-cv',
		segmentIconColor: 'var(--neutral-700, #323848)',
	},
	[ProfilFollowingType.CV_ANONYMOUS_DELETED]: {
		icon: 'user-circle-fill',
		segmentIcon: 'doc-cv',
		segmentIconColor: 'var(--neutral-700, #323848)',
	},
	[ProfilFollowingType.DATA_UPDATED]: { icon: 'user-circle-fill-edit' },
};

const CV_TYPES = new Set([
	ProfilFollowingType.CV_UPDATED,
	ProfilFollowingType.CV_DELETED,
	ProfilFollowingType.CV_ANONYMOUS_UPDATED,
	ProfilFollowingType.CV_ANONYMOUS_DELETED,
]);

const t = (key: string): string => i18n.global.t(key);

export interface ProfilFollowingInput extends FollowingBaseInput {
	type: ProfilFollowingType;
	labelSuffix?: string;
	to?: RouteLocationRaw;
	onCvClick?: (event: MouseEvent) => void;
	summary?: string | string[];
	onViewChanges?: (event: MouseEvent) => void;
}

const buildDataUpdatedText = (
	summary: string | string[] | undefined
): string => {
	if (Array.isArray(summary)) {
		const header = t('following.labels.profil.dataUpdatedWithFields');
		const fieldList = summary.map((field) => `  - ${field}`).join('\n');
		return `${header}\n${fieldList}`;
	}

	return summary ?? t('following.labels.profil.dataUpdated');
};

const buildDataUpdatedSegments = (
	summary: string | string[] | undefined
): FollowingSegment[] => {
	return [
		createTextSegment(buildDataUpdatedText(summary), {
			multiline: Array.isArray(summary),
		}),
	];
};

const buildDataUpdatedRightAction = (
	onViewChanges?: (event: MouseEvent) => void
): FollowingRightAction | undefined => {
	if (!onViewChanges) return undefined;
	return {
		label: t('following.labels.profil.viewChanges'),
		onClick: onViewChanges,
	};
};

export const buildFollowingLineProfil = (
	input: ProfilFollowingInput
): FollowingLineGeneratorResult => {
	const config = TYPE_CONFIGS[input.type];

	if (input.type === ProfilFollowingType.DATA_UPDATED) {
		return {
			icon: config.icon,
			segments: buildDataUpdatedSegments(input.summary),
			detail: input.detail,
			rightAction: buildDataUpdatedRightAction(input.onViewChanges),
		};
	}

	const isCvType = CV_TYPES.has(input.type);
	const segmentOptions = {
		leftIcon: config.segmentIcon,
		iconColor: config.segmentIconColor,
	};
	const baseLabel = t(`following.labels.profil.${input.type}`);
	const label = input.labelSuffix
		? `${baseLabel} - "${input.labelSuffix}"`
		: baseLabel;

	const mainSegment = (() => {
		if (isCvType && input.onCvClick) {
			return createActionSegment(label, {
				...segmentOptions,
				onClick: input.onCvClick,
			});
		}
		if (input.to) {
			return createLinkSegment(label, { ...segmentOptions, to: input.to });
		}
		return createReadonlyLabelSegment(label, segmentOptions);
	})();

	return {
		icon: config.icon,
		segments: [mainSegment],
		detail: input.detail,
	};
};
